import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, Grid3X3, List, MapPin, X, Sparkles } from 'lucide-react';
import { Button } from '@/propertyai/components/ui/button';
import { Input } from '@/propertyai/components/ui/input';
import { Label } from '@/propertyai/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/propertyai/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/propertyai/components/ui/sheet';
import { Badge } from '@/propertyai/components/ui/badge';
import { ScrollArea } from '@/propertyai/components/ui/scroll-area';
import PropertyCard from '@/propertyai/components/PropertyCard';
import MapView from '@/propertyai/components/MapView';
import AIChat from '@/propertyai/components/AIChat';
import { searchProperties, getCities } from '@/propertyai/lib/api';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('split');
  const [chatOpen, setChatOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [cities, setCities] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filters from URL
  const [filters, setFilters] = useState({
    query: searchParams.get('query') || '',
    city: searchParams.get('city') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    beds_min: searchParams.get('beds_min') || '',
    baths_min: searchParams.get('baths_min') || '',
    property_type: searchParams.get('property_type') || '',
    sort_by: searchParams.get('sort_by') || 'newest',
  });

  const [bounds, setBounds] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    getCities().then(res => setCities(res.data.cities || [])).catch(() => {});
  }, []);

  // Initial load
  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = useCallback(async (customBounds, customFilters) => {
    setLoading(true);
    try {
      const params = { ...(customFilters || filters), limit: 100 };
      
      // Add bounds if available
      const b = customBounds || bounds;
      if (b) {
        params.ne_lat = b.ne_lat;
        params.ne_lng = b.ne_lng;
        params.sw_lat = b.sw_lat;
        params.sw_lng = b.sw_lng;
      }

      // Clean empty params and sentinel values
      Object.keys(params).forEach(k => {
        if (params[k] === '' || params[k] === null || params[k] === undefined
            || params[k] === 'all_cities' || params[k] === 'all_types' || params[k] === 'any_beds' || params[k] === 'any_baths') {
          delete params[k];
        }
      });

      const res = await searchProperties(params);
      setProperties(res.data.properties || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Search error', e);
    } finally {
      setLoading(false);
    }
  }, [filters, bounds]);

  const handleBoundsChange = useCallback((newBounds) => {
    setBounds(newBounds);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchProperties(newBounds);
    }, 300);
  }, [fetchProperties]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Update URL
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
    fetchProperties(bounds, filters);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setFiltersOpen(false);
    fetchProperties(bounds, filters);
  };

  const resetFilters = () => {
    const reset = { query: '', city: '', min_price: '', max_price: '', beds_min: '', baths_min: '', property_type: '', sort_by: 'newest' };
    setFilters(reset);
    setSearchParams({});
    fetchProperties(bounds, reset);
  };

  const handlePropertyClick = (prop) => {
    navigate(`/property-ai/homes/${prop.id}`);
  };

  // Determine map center from first property or default
  const mapCenter = properties.length > 0 && properties[0].lat
    ? [properties[0].lat, properties[0].lng]
    : filters.city
      ? getCityCenter(filters.city)
      : [39.8283, -98.5795];

  const mapZoom = filters.city ? 11 : bounds ? undefined : 4;

  const activeFiltersCount = [filters.min_price, filters.max_price, filters.beds_min, filters.baths_min, filters.property_type].filter(Boolean).length;

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col">
      {/* Search Header */}
      <div className="sticky top-14 z-40 bg-background border-b border-border px-4 py-2.5">
        <div className="max-w-full flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex-1 flex items-center gap-2">
            <div className="flex items-center gap-2 flex-1 border border-border rounded-xl px-3 py-1.5 bg-background">
              <Search className="w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={filters.query}
                onChange={(e) => handleFilterChange('query', e.target.value)}
                placeholder='Search by city, neighborhood, or features...'
                className="flex-1 bg-transparent border-none outline-none text-sm"
                data-testid="search-input"
              />
              {filters.query && (
                <button onClick={() => handleFilterChange('query', '')} className="text-muted-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Button type="submit" size="sm" className="rounded-xl" data-testid="search-submit">
              Search
            </Button>
          </form>

          {/* Filter chips */}
          <div className="hidden md:flex items-center gap-2">
            <Select value={filters.city} onValueChange={(v) => { handleFilterChange('city', v); setTimeout(() => fetchProperties(bounds, { ...filters, city: v }), 100); }}>
              <SelectTrigger className="w-[140px] rounded-xl text-xs h-8">
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_cities">All Cities</SelectItem>
                {cities.map(c => (
                  <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.beds_min} onValueChange={(v) => { handleFilterChange('beds_min', v); setTimeout(() => fetchProperties(bounds, { ...filters, beds_min: v }), 100); }}>
              <SelectTrigger className="w-[100px] rounded-xl text-xs h-8">
                <SelectValue placeholder="Beds" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any_beds">Any Beds</SelectItem>
                {[1,2,3,4,5].map(b => (
                  <SelectItem key={b} value={String(b)}>{b}+ beds</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filters button */}
          <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-xl gap-1.5" data-testid="filters-open-button">
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Filters
                {activeFiltersCount > 0 && (
                  <Badge className="h-5 w-5 p-0 flex items-center justify-center text-[10px]">{activeFiltersCount}</Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[340px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="space-y-5 mt-6">
                <div>
                  <Label className="text-sm">City</Label>
                  <Select value={filters.city} onValueChange={(v) => handleFilterChange('city', v)}>
                    <SelectTrigger className="mt-1.5 rounded-xl">
                      <SelectValue placeholder="All Cities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_cities">All Cities</SelectItem>
                      {cities.map(c => (
                        <SelectItem key={c.name} value={c.name}>{c.name}, {c.state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Min Price</Label>
                    <Input type="number" value={filters.min_price} onChange={(e) => handleFilterChange('min_price', e.target.value)} placeholder="No min" className="mt-1.5 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm">Max Price</Label>
                    <Input type="number" value={filters.max_price} onChange={(e) => handleFilterChange('max_price', e.target.value)} placeholder="No max" className="mt-1.5 rounded-xl" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Beds (min)</Label>
                    <Select value={filters.beds_min} onValueChange={(v) => handleFilterChange('beds_min', v)}>
                      <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any_beds">Any</SelectItem>
                        {[1,2,3,4,5].map(b => (<SelectItem key={b} value={String(b)}>{b}+</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Baths (min)</Label>
                    <Select value={filters.baths_min} onValueChange={(v) => handleFilterChange('baths_min', v)}>
                      <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any_baths">Any</SelectItem>
                        {[1,2,3,4].map(b => (<SelectItem key={b} value={String(b)}>{b}+</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Property Type</Label>
                  <Select value={filters.property_type} onValueChange={(v) => handleFilterChange('property_type', v)}>
                    <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue placeholder="All Types" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_types">All Types</SelectItem>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="condo">Condo</SelectItem>
                      <SelectItem value="townhouse">Townhouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Sort By</Label>
                  <Select value={filters.sort_by} onValueChange={(v) => handleFilterChange('sort_by', v)}>
                    <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest</SelectItem>
                      <SelectItem value="price_asc">Price: Low to High</SelectItem>
                      <SelectItem value="price_desc">Price: High to Low</SelectItem>
                      <SelectItem value="sqft">Square Footage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className="flex-1 rounded-xl" onClick={resetFilters} data-testid="filters-reset-button">Reset</Button>
                  <Button className="flex-1 rounded-xl" onClick={applyFilters} data-testid="filters-apply-button">Apply</Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          {/* Sort */}
          <Select value={filters.sort_by} onValueChange={(v) => { handleFilterChange('sort_by', v); setTimeout(() => fetchProperties(bounds, { ...filters, sort_by: v }), 100); }}>
            <SelectTrigger className="w-[140px] rounded-xl text-xs h-8 hidden lg:flex">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_asc">Price: Low</SelectItem>
              <SelectItem value="price_desc">Price: High</SelectItem>
            </SelectContent>
          </Select>

          {/* Results count */}
          <span className="text-xs text-muted-foreground hidden sm:block whitespace-nowrap">
            {total.toLocaleString()} homes
          </span>

          {/* AI Chat */}
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl gap-1.5 bg-primary/5 border-primary/20 text-primary hover:bg-primary/10"
            onClick={() => setChatOpen(true)}
            data-testid="ai-chat-open-button"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">AI</span>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[420px_1fr] xl:grid-cols-[460px_1fr] overflow-hidden">
        {/* Results List */}
        <div className="overflow-y-auto border-r border-border">
          <div className="p-3 space-y-3">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border bg-background overflow-hidden">
                  <div className="aspect-[16/10] shimmer" />
                  <div className="p-4 space-y-2">
                    <div className="h-5 shimmer rounded w-1/3" />
                    <div className="h-4 shimmer rounded w-2/3" />
                  </div>
                </div>
              ))
            ) : properties.length === 0 ? (
              <div className="text-center py-16">
                <MapPin className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No properties found</h3>
                <p className="text-sm text-muted-foreground mb-4">Try adjusting your filters or zooming out on the map.</p>
                <Button variant="outline" className="rounded-xl" onClick={resetFilters}>Reset Filters</Button>
              </div>
            ) : (
              properties.map(prop => (
                <div
                  key={prop.id}
                  onMouseEnter={() => setHoveredId(prop.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  <PropertyCard property={prop} />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Map */}
        <div className="hidden lg:block">
          <MapView
            properties={properties}
            center={mapCenter}
            zoom={mapZoom || 10}
            onBoundsChange={handleBoundsChange}
            hoveredId={hoveredId}
            onPropertyClick={handlePropertyClick}
            className="h-full rounded-none border-0"
          />
        </div>
      </div>

      {/* AI Chat Sheet */}
      <Sheet open={chatOpen} onOpenChange={setChatOpen}>
        <SheetContent className="w-[420px] sm:w-[480px] p-0">
          <AIChat isOpen={chatOpen} onClose={() => setChatOpen(false)} context={{ search: true }} />
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Helper for city centers
function getCityCenter(city) {
  const centers = {
    'Austin': [30.2672, -97.7431], 'Miami': [25.7617, -80.1918],
    'Denver': [39.7392, -104.9903], 'Seattle': [47.6062, -122.3321],
    'Phoenix': [33.4484, -112.0740], 'Nashville': [36.1627, -86.7816],
    'Raleigh': [35.7796, -78.6382], 'Tampa': [27.9506, -82.4572],
    'Charlotte': [35.2271, -80.8431], 'Portland': [45.5152, -122.6784],
  };
  return centers[city] || [39.8283, -98.5795];
}

